import { Injectable } from '@angular/core';
import { ProductsSearchService } from '@clubhub/ignite/services';
import { ProductFeeType, feesDisplayNames } from '@shared/constants/product-fees';
import { BridgingProduct, CustomFee, Product, ProductFeeStructure } from '@shared/models';

@Injectable({
  providedIn: 'root',
})
export class ProductsUtils {
  public getProductDescription(product: Product | BridgingProduct): string {
    return (product as Product).productDescription ?? (product as BridgingProduct).description;
  }

  public getProductCode(product: Product | BridgingProduct): string {
    return (product as Product).productCode ?? (product as BridgingProduct).code;
  }

  public getProductTrueCost(product: Product | BridgingProduct): number | null | undefined {
    return this.isBridgingProduct(product) ? (product as BridgingProduct).trueCost : (product as Product).trueCost;
  }

  private isBridgingProduct(product: Product | BridgingProduct): boolean {
    return 'isRegulated' in product && 'bridgeToLet' in product;
  }

  public calculateTotalFees(
    product: Product,
    productsSearchService: ProductsSearchService,
    includeTrueCostErc = false,
  ): number {
    return this.calculateProductTotalFees(product) +
      Math.max(this.getAssumedLegalFee(product, productsSearchService) ?? 0, 0) +
      this.getTotalCustomFeeAmount(product, productsSearchService) +
      (includeTrueCostErc ? product.trueCostErc ?? 0 : 0);
  }

  public calculateProductTotalFees(product: Product): number {
    return Object.values(product.fees).filter(fee => fee > 0).reduce((total, fee) => total + (fee || 0), 0);
  }

  public getProductsFees(product: Product, productsSearchService: ProductsSearchService, includeTrueCostErc = false) {
    const customFees = [...productsSearchService.getCustomFees(), ...(product.brokerFees ?? []), ...(product.assumedLegalFees ?? [])];

    const productFees = (Object.entries(product.fees) as [string, number | undefined][])
      // Fees with a value of -1 are considered 'refer to lender' and should not be included in the total fees list.
      .filter(([, feeValue]) => feeValue !== -1)
      .map(([key, feeValue]) => {
        const feeKey = Number(key) as keyof typeof feesDisplayNames;
        return {
          name: feesDisplayNames[feeKey],
          value: feeValue ?? 0,
        };
      });

    customFees.forEach(customFee => {
      productFees.push({
        name: customFee.feeName,
        value: this.getCustomFeeAmount(customFee, productsSearchService),
      });
    });

    if (includeTrueCostErc && (product.trueCostErc ?? 0) > 0) {
      productFees.push({
        name: feesDisplayNames[ProductFeeType.EarlyRepayment],
        value: product.trueCostErc ?? 0,
      });
    }

    return productFees;
  }

  public getAssumedLegalFee(product: Product, productsSearchService: ProductsSearchService): number | null {
    const assumedLegalFeeRow = product.assumedLegalFees?.[0];
    if (!assumedLegalFeeRow) {
      return 0;
    }

    return this.getCustomFeeAmount(assumedLegalFeeRow, productsSearchService);
  }

  public getCustomFeeAmount(fee: CustomFee, productsSearchService: ProductsSearchService): number {
    return fee.feeStructure === ProductFeeStructure.Percentage
      ? fee.feeAmount * (productsSearchService.getLoanAmount() ?? 0)
      : fee.feeAmount;
  }

  public getTotalCustomFeeAmount(product: Product, productsSearchService: ProductsSearchService): number {
    const customFees = [
      ...productsSearchService.getCustomFees(),
      ...(product.brokerFees ?? []),
    ];
    return customFees
      .map(fee => this.getCustomFeeAmount(fee, productsSearchService))
      .reduce((partialSum, fee) => partialSum + fee, 0);
  }

  public getRevertRateColumnValue(product: Product) {
    if (!product) {
      return null;
    }

    return !!product.forTerm ? null : product.revertRate;
  }
}
